import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEventBySlug, createRegistration } from '../../services/apiClient';
import type { Event, RegistrationFormData } from '../../types';
import { validateRegistrationForm, hasErrors } from '../../lib/validation';
import { Button } from '../../components/ui/Button';
import styles from './RegisterPage.module.css';

const EMPTY_FORM: RegistrationFormData = {
  name: '',
  email: '',
  phone: '',
  team_name: '',
  notes: '',
};

export function RegisterPage() {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [form, setForm] = useState<RegistrationFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventSlug) return;
    getEventBySlug(eventSlug).then((r) => r.data && setEvent(r.data));
  }, [eventSlug]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateRegistrationForm(form);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }
    if (!event) return;
    setSubmitting(true);
    setSubmitError(null);
    const res = await createRegistration(form, event.id);
    setSubmitting(false);
    if (res.success) {
      setConfirmed(true);
    } else {
      setSubmitError(res.error ?? 'Something went wrong. Please try again.');
    }
  };

  if (!event) return <div className={styles.loading}>Loading…</div>;

  if (confirmed) {
    return (
      <div className={styles.confirmation}>
        <div className={styles.confirmCard}>
          <div className={styles.confirmIcon} aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="18" stroke="var(--color-success)" strokeWidth="2" />
              <path d="M12 20l6 6 10-12" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 className={styles.confirmTitle}>Registration Confirmed</h2>
          <p className={styles.confirmBody}>
            Thanks, <strong>{form.name}</strong>. Your registration for{' '}
            <strong>{event.title}</strong> has been received. A confirmation has been sent to{' '}
            <strong>{form.email}</strong>.
          </p>
          <Link to="/events" className={styles.confirmBack}>Back to Events</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className="container">
          <Link to={`/events/${event.slug}`} className={styles.back}>← {event.title}</Link>
          <h1 className={styles.title}>Event Registration</h1>
        </div>
      </div>

      <div className="container">
        <div className={styles.layout}>
          <div className={styles.formWrap}>
            <form onSubmit={handleSubmit} noValidate className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="name" className={styles.label}>Full Name <span className={styles.req}>*</span></label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  className={[styles.input, errors.name ? styles.inputError : ''].join(' ')}
                  autoComplete="name"
                />
                {errors.name && <p className={styles.error}>{errors.name}</p>}
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label htmlFor="email" className={styles.label}>Email <span className={styles.req}>*</span></label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className={[styles.input, errors.email ? styles.inputError : ''].join(' ')}
                    autoComplete="email"
                  />
                  {errors.email && <p className={styles.error}>{errors.email}</p>}
                </div>
                <div className={styles.field}>
                  <label htmlFor="phone" className={styles.label}>Phone</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className={[styles.input, errors.phone ? styles.inputError : ''].join(' ')}
                    autoComplete="tel"
                  />
                  {errors.phone && <p className={styles.error}>{errors.phone}</p>}
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="team_name" className={styles.label}>Team Name</label>
                <input
                  id="team_name"
                  name="team_name"
                  type="text"
                  value={form.team_name}
                  onChange={handleChange}
                  className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="notes" className={styles.label}>Notes or Special Requests</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={4}
                  className={styles.textarea}
                />
              </div>

              {submitError && <p className={styles.submitError}>{submitError}</p>}

              <Button type="submit" size="lg" loading={submitting}>
                Submit Registration
              </Button>
            </form>
          </div>

          <aside className={styles.eventSummary}>
            <h3 className={styles.summaryTitle}>{event.title}</h3>
            <p className={styles.summaryLocation}>{event.location}</p>
          </aside>
        </div>
      </div>
    </div>
  );
}
